import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';

import SpinnerBlock from './SpinnerBlock';
import Pagination from './Pagination';

import { fetchGetFieldList } from '../../actions/imports';

import { PRODUCT_JOURNAL_ON_PAGE } from '../../config';
import Button from './Button';
import { showModal } from '../../actions/modal';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  table.table td {
    color: #72747b;
    font-size: 12px;
  }
  .plus-td {
    font-size: 16px;
    padding-right: 0 !important;
    cursor: pointer;
  }
  .open-task-tr {
    td {
      border-top: none;
      padding-top: 20px !important;
    }
  }
  .btn-report {
    display: block;
    margin: 0 auto;
  }
  .task-info {
    position: relative;
    width: 100%;
    font-size: 12px;
    min-height: 150px;
    margin-bottom: 20px;
    .task-info-data {
      .task-info-item {
        display: flex;
        padding: 12px 19px;
        background-color: #f6f9fa;
        border-bottom: 1px solid #ebebeb;
        &:last-child {
          border-bottom: none;
          border-radius: 0 0 5px 5px;
        }
        > div {
          &:first-child {
            width: 150px;
            font-weight: bold;
          }
          &:last-child {
            margin-left: 20px;
            color: #343a40;
          }
        }
      }
    }
    .task-info-juxtaposition {
      margin-top: 20px;
      .task-info-item {
        display: flex;
        padding: 12px 19px;
        background-color: #f6f9fa;
        border-bottom: 1px solid #ebebeb;
        color: #343a40;
        > div {
          &:first-child {
            min-width: 150px;
          }
          &:last-child {
            margin-left: 20px;
          }
        }
        &:last-child {
          border-bottom: none;
          border-radius: 0 0 5px 5px;
        }
      }
    }
    .task-info-header {
      border-radius: 5px 5px 0 0;
      padding: 12px 19px;
      border-bottom: 1px solid #ebebeb;
      background-color: #f1f5f7;
      font-weight: bold;
      font-size: 13px;
      color: #72747b;
    }
  }

  .header-buttons {
    display: flex;
  }

  .import-button {
    margin-left: 20px;
  }
`;

const getProgressStr = (task, processedLabel, translate) => {
  const { startTime, itemsProcessedCount, rowsTotal } = task;
  if (!startTime) {
    return translate('Не начата, в очереди на выполнение');
  }
  if (itemsProcessedCount < rowsTotal) {
    return `${translate(processedLabel)}: ${Number(itemsProcessedCount)} ${translate(
      'из',
    )} ${Number(rowsTotal)} ${translate('строк')}`;
  }
  if (Number(itemsProcessedCount) === Number(rowsTotal)) {
    return `${translate(processedLabel)}: ${Number(rowsTotal)} ${translate('строк')}`;
  }
};

const ProgressBarWrapper = styled('div')`
  width: 200px;
  position: relative;
  .progress-bar-text {
    position: absolute;
    top: -1px;
    left: 0px;
    font-size: 11px;
    width: 100%;
    text-align: center;
    color: #232323;
    padding: 0 3px;
  }
`;

const ProgressBar = (props) => {
  const {
    itemsProcessedCount = 0,
    rowsTotal,
    progressPercentage,
    itemsFailedCount = 0,
    emptyRowsCount,
    finishTime,
    translate,
  } = props;

  if (!itemsProcessedCount && !finishTime) {
    return (
      <ProgressBarWrapper className="progress">
        <div className="progress-bar" style={{ width: `0%` }} />
        <div className="progress-bar-text">0%</div>
      </ProgressBarWrapper>
    );
  }

  if (itemsProcessedCount + emptyRowsCount < rowsTotal) {
    return (
      <ProgressBarWrapper className="progress">
        <div
          className="progress-bar"
          style={{ width: `${progressPercentage}%`, backgroundColor: '#f7b84b' }}
        />
        <div className="progress-bar-text">{progressPercentage}%</div>
      </ProgressBarWrapper>
    );
  }

  if (itemsProcessedCount <= rowsTotal) {
    if (itemsFailedCount === 0) {
      return (
        <ProgressBarWrapper className="progress">
          <div className="progress-bar" style={{ width: `100%`, backgroundColor: '#1abc9c' }} />
          <div className="progress-bar-text" style={{ color: 'white' }}>
            {translate('Завершено')}
          </div>
        </ProgressBarWrapper>
      );
    } else {
      return (
        <ProgressBarWrapper className="progress">
          <div className="progress-bar" style={{ width: '100%', backgroundColor: '#f1556c' }} />
          <div className="progress-bar-text" style={{ color: 'white' }}>
            {translate('Завершено с ошибками')}
          </div>
        </ProgressBarWrapper>
      );
    }
  }

  return null;
};

const JournalTable = (props) => {
  const {
    templateType,
    processedLabel = 'Обработано',
    newTaskLabel,
    newTaskUrl,
    taskName,
    getTaskData,
    matchingWithName,
    getTasksAction,
    getTaskDataAction,
    showSummary = true,
    detailsTitle,
    showReport,
    showSuccessReport = () => false,
    reportUrl,
    taskTimePropName = 'date',
    title,
  } = props;

  const { t } = useAutoTranslation();
  const translate = t;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [fieldMatching, setFieldMatching] = useState({});
  const [openTasks, setOpenTasks] = useState({});
  const [tasksData, setTasksData] = useState({});

  const [page, setPage] = useState(0);
  const [recordsTotal, setRecordsTotal] = useState(0);

  const getImportTasks = async () => {
    setLoading(true);

    const newTasksData = await dispatch(
      getTasksAction({
        size: PRODUCT_JOURNAL_ON_PAGE,
        page,
        templateType,
      }),
    );

    setTasks(newTasksData.tasks);
    setRecordsTotal(newTasksData.recordsTotal);

    setLoading(false);
  };

  useEffect(() => {
    (async function () {
      const result = await Promise.all([
        dispatch(fetchGetFieldList(templateType)),
        getImportTasks(),
      ]);

      const newFieldMatching = result[0];
      setFieldMatching(newFieldMatching);
    })();
  }, []);

  useEffect(() => {
    (async function () {
      await getImportTasks();
    })();
  }, [page]);

  const handleToggleTask = async (taskId) => {
    const currentState = openTasks[taskId];
    const newObj = { ...openTasks, [taskId]: !currentState };
    setOpenTasks(newObj);
    if (!currentState && !tasksData[taskId]) {
      const taskData = await dispatch(getTaskDataAction(taskId));
      const newData = { ...tasksData, [taskId]: taskData };
      setTasksData(newData);
    }
  };

  //TODO: Move into helpers
  const openImportChestnyznakModal = () => {
    dispatch(
      showModal('ImportChestnyznakNumbersModal', {
        disableFileTypeToggler: true,
      }),
    );
  };

  return (
    <Wrapper className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-12 header-buttons">
                <Link
                  className="btn btn-danger"
                  to={newTaskUrl}
                  style={{ width: '140px', display: 'block' }}
                >
                  <i className="fe-plus" />
                  {newTaskLabel}
                </Link>
                {title === translate('Задачи импорта товаров') && (
                  <Button
                    text={translate('Импорт пула номеров «Честный Знак»')}
                    onClick={openImportChestnyznakModal}
                    className="import-button"
                  ></Button>
                )}
              </div>
            </div>
            <div className="bootstrap-table">
              <SpinnerBlock show={loading} />
              <div className="fixed-table-container">
                <div className="fixed-table-body">
                  <table className="table table-centered">
                    <thead className="thead-light">
                      <tr>
                        <th />
                        <th>{translate('Задача')}</th>
                        <th>{translate('Прогресс')}</th>
                        <th>{translate('Время выполнения')}</th>
                        {showSummary ? <th>{translate('Сводка')}</th> : null}
                        <th />
                      </tr>
                    </thead>

                    <tbody>
                      {tasks.map((t) => (
                        <>
                          <tr key={t.id} data-testid="journal-tr">
                            <td className="plus-td" onClick={() => handleToggleTask(t.id)}>
                              <i className={openTasks[t.id] ? 'fe-minus' : 'fe-plus'} />
                            </td>
                            <td onClick={() => handleToggleTask(t.id)}>
                              {t.id}{' '}
                              {t[taskTimePropName]
                                ? `${translate('от')} ${moment(t[taskTimePropName]).format(
                                    'DD.MM.YYYY HH:mm',
                                  )}`
                                : null}
                              <br />
                              {translate(taskName)}
                            </td>
                            <td>
                              <ProgressBar {...t} translate={translate} />
                              <div style={{ marginTop: '5px' }}>
                                {getProgressStr(t, processedLabel, translate)}
                              </div>
                            </td>
                            <td>
                              {t.startTime
                                ? `${translate('Начата')}: ${moment(t.startTime).format(
                                    'DD.MM.YYYY HH:mm',
                                  )}`
                                : null}
                              <br />
                              {t.finishTime
                                ? `${translate('Завершена')}: ${moment(t.finishTime).format(
                                    'DD.MM.YYYY HH:mm',
                                  )}`
                                : null}
                            </td>
                            {showSummary ? (
                              <td>
                                {translate('Создано')}: {t.itemsCreatedCount || 0}
                                <br />
                                {translate('Обновлено')}: {t.itemsUpdatedCount || 0}
                                <br />
                                {translate('Пропущено из-за ошибок')}: {t.itemsFailedCount || 0}
                                <br />
                                {t.emptyRowsCount
                                  ? `${translate('Пустых строк')}: ${t.emptyRowsCount}`
                                  : null}
                              </td>
                            ) : null}
                            <td>
                              {showSuccessReport(t) ? (
                                <a
                                  className="btn btn-light btn-report"
                                  href={t.fileUrl}
                                  target="_blank"
                                >
                                  {translate('Отчёт')}
                                </a>
                              ) : null}
                              {showReport(t) ? (
                                <Link
                                  className="btn btn-light btn-report"
                                  to={`${reportUrl}/${t.id}`}
                                >
                                  {translate('Отчёт об ошибках')}
                                </Link>
                              ) : null}
                            </td>
                          </tr>
                          {openTasks[t.id] ? (
                            <tr className="no-hover open-task-tr" data-testid="open-task-tr">
                              <td />
                              <td colSpan={5} style={{ paddingRight: 0 }}>
                                <div className="task-info">
                                  {tasksData[t.id] ? (
                                    <>
                                      <div className="task-info-data">
                                        <div className="task-info-header">{detailsTitle}</div>
                                        <div className="task-info-item">
                                          <div>{translate('Файл')}</div>
                                          <div>
                                            <a href={tasksData[t.id].fileUrl} target="_blank">
                                              {tasksData[t.id].fileUrl}
                                            </a>
                                          </div>
                                        </div>
                                        {getTaskData(tasksData[t.id]).map((t, i) => (
                                          <div
                                            key={`task-info-item-${i}`}
                                            className="task-info-item"
                                            data-testid={`task-info-item-${i}`}
                                          >
                                            <div>{t.label}</div>
                                            <div>{t.value}</div>
                                          </div>
                                        ))}
                                      </div>
                                      {tasksData[t.id].fieldMatching ? (
                                        <div className="task-info-juxtaposition">
                                          <div className="task-info-header">
                                            {translate('Сопоставление полей')}
                                          </div>
                                          <div className="task-info-item">
                                            <div style={{ color: '#72747b', fontWeight: 'bold' }}>
                                              {translate('Поле кактус')}
                                            </div>
                                            <div style={{ color: '#72747b', fontWeight: 'bold' }}>
                                              {translate('Поле в файле импорта')}
                                            </div>
                                          </div>
                                          {Object.keys(tasksData[t.id].fieldMatching).map((key) => (
                                            <div
                                              className="task-info-item"
                                              data-testid="task-info-item"
                                            >
                                              <div>
                                                {matchingWithName && fieldMatching[key] ? (
                                                  <>
                                                    {fieldMatching[key]}
                                                    <br />
                                                  </>
                                                ) : null}
                                                {key}
                                              </div>
                                              <div>{tasksData[t.id].fieldMatching[key]}</div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    <SpinnerBlock show={true} />
                                  )}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    total={recordsTotal}
                    currentPage={page}
                    perPage={PRODUCT_JOURNAL_ON_PAGE}
                    onPageSelect={(pageIndex) => setPage(pageIndex)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default JournalTable;
